import './css/CustomHeatMap.css';
import { useState, useEffect, useRef } from 'react';

const CustomHeatMap = ({ contributions, selectedYear, selectedTool, newCommits}) => {
    const [calendar, setCalendar] = useState([]);

    const formatDateToWords = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        return date.toLocaleString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    useEffect(() => {
        // console.log(newCommits)
    }, [newCommits])

    useEffect(() => {
        selectedYear = parseInt(selectedYear, 10);
        const DAYS_IN_WEEK = 7;
        const WEEKS_IN_YEAR = 53;
        const calendar = Array.from({ length: DAYS_IN_WEEK }, () => Array(WEEKS_IN_YEAR).fill(null));

        if (!contributions || typeof contributions !== 'object') {
            setCalendar([]);
            return;
        }
        if (!selectedYear || isNaN(selectedYear)) {
            console.error('Invalid selectedYear:', selectedYear);
            setCalendar([]);
            return;
        }

        const colorMap = {
            "#ebedf0": "#141b23",
            "#9be9a8": "#043915",
            "#40c463": "#186c2f",
            "#30a14e": "#2f9f43",
            "#216e39": "#56d264"
        };

        // Build lookup from date string to count
        const data = (contributions[selectedYear] || []).reduce((acc, item) => {
            let baseColor = item.color || 'transparent';
            if (colorMap[baseColor]) {
                baseColor = colorMap[baseColor];
            }
            acc[item.date] = {
                contributionCount: item.contributionCount,
                date: item.date,
                // color: item.contributionCount === 0 ? '#ffffff08' : baseColor
                // color: item.contributionCount === 0 ? 'transparent' : baseColor
                color: item.contributionCount === 0 ? '#ffffff18' : baseColor
            };
            return acc;
        }, {});

        const startDate = new Date(selectedYear, 0, 1);
        const endDate = new Date(selectedYear + 1, 0, 1);
        const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

        let currentWeek = 0;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(selectedYear, 0, 1 + i);
            const weekDay = date.getDay();
            const isoDate = date.toISOString().slice(0, 10);

            const value = data[isoDate] !== undefined
                ? data[isoDate]
                : {
                    contributionCount: 0,
                    date: isoDate,
                    color: '#0D100F'
                };

            calendar[weekDay][currentWeek] = value;

            if (weekDay === 6) currentWeek++;
        }

        setCalendar(calendar);

    }, [contributions, selectedYear]);

    useEffect(() => {
        // console.log(calendar);
    }, [calendar]);

    // Helper to interpolate color between min and max green
    const getContributionColor = (level) => {
        if (level === 0) return 'transparent';
        
        const minColor = { r: 14, g: 68, b: 41 };   // #0e4429
        const maxColor = { r: 57, g: 211, b: 83 };  // #39d353
        
        const ratio = level / 10;
        
        const r = Math.round(minColor.r + (maxColor.r - minColor.r) * ratio);
        const g = Math.round(minColor.g + (maxColor.g - minColor.g) * ratio);
        const b = Math.round(minColor.b + (maxColor.b - minColor.b) * ratio);
        
        return `rgb(${r}, ${g}, ${b})`;
    };

    const containerRef = useRef(null);

    const [tooltip, setTooltip] = useState({
        show: false,
        x: 0,
        y: 0,
        date: '',
        count: 0,
        newCount: 0
    });

    const handleMouseEnter = (e, day) => {
        if (!day || !day.date) return;
        if (selectedTool !== 0) return;
        
        const rect = e.currentTarget.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        
        const newCount = newCommits[selectedYear] && newCommits[selectedYear][day.date] 
            ? newCommits[selectedYear][day.date].contributionCount 
            : 0;

        const scale = containerRect.width / containerRef.current.offsetWidth;
        const relativeX = (rect.left - containerRect.left) / scale;
        const relativeY = (rect.top - containerRect.top) / scale;
        const cellWidth = rect.width / scale;
        const cellHeight = rect.height / scale;

        setTooltip({
            show: true,
            x: relativeX,
            y: relativeY,
            date: day.date,
            count: day.contributionCount,
            newCount: newCount
        });
    };

    const handleMouseLeave = () => {
        setTooltip(prev => ({ ...prev, show: false }));
    };

    return (
        <div className="custom-heatmap" ref={containerRef}>
            <div className="map">
                {calendar.map((week, weekIndex) => (
                    <div className="week" key={`week-${weekIndex}`}>
                        {week.map((day, dayIndex) => (
                            <div className='day' key={`day-${weekIndex}-${dayIndex}`}>
                                {
                                day ?
                                    <div 
                                        className={`square`}
                                        date={day?.date}
                                        count={day?.contributionCount}
                                        onMouseEnter={(e) => handleMouseEnter(e, day)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <div className={`bottom-cell ${day.contributionCount > 0 ? "original" : ""}`}>
                                            <div className="inner" style={{ backgroundColor: day.color }}></div>
                                        </div>
                                        <div className="top-cell">
                                            <div 
                                                className="inner" 
                                                style={{ 
                                                    backgroundColor: newCommits[selectedYear] && newCommits[selectedYear][day?.date] 
                                                        ? getContributionColor(newCommits[selectedYear][day?.date].level)
                                                        : 'transparent' 
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    :
                                    <div className={`empty`}></div>
                                }
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div 
                className="heatmap-tooltip"
                style={{ 
                    left: tooltip.x, 
                    top: tooltip.y,
                    opacity: tooltip.show ? 1 : 0
                }}
            >
                <div className="tooltip-section">
                    <span className="label">Current:</span>
                    <span className="value">{`${tooltip.count} commits`}</span>
                </div>
                {tooltip.newCount > 0 && (
                    <div className="tooltip-section new">
                        <span className="label">New:</span>
                        <span className="value">{`+${tooltip.newCount} commits`}</span>
                    </div>
                )}
                <div className="tooltip-date">{formatDateToWords(tooltip.date)}</div>
            </div>
        </div>
    );
};

export default CustomHeatMap;
